import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { Grid } from "@mui/material";
import { cleanupTokenData } from "../utils/auth";
import { setPageTitle, getPageTitleFromRoute, updateCompanyNameFromToken } from "../utils/pageTitle";
import { LoaderComponent } from "./Icon/Index";
import { useUserType } from "../contexts/UserTypeContext";
import { getItem, setItem, removeItem } from "../utils/storage";

const ProtectedRoute = ({
  children,
  requiredRole = null,
  redirectTo = "/auth/login",
  tokenKey = "adminToken"
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  useEffect(() => {
    // JOB-3245: Don't block auth on the title side-effect. updateCompanyNameFromToken
    // fires-and-forgets so checkAuthentication runs immediately and the page can
    // render while the title updates in the background.
    updateCompanyNameFromToken();
    checkAuthentication();
  }, []);
  useEffect(() => {
    const title = getPageTitleFromRoute(location?.pathname);
    setPageTitle(title);

    // Check if impersonating and trying to access superadmin routes - redirect immediately
    if (requiredRole === "SuperAdmin") {
      const isImpersonated = getItem("isImpersonated") === "true";
      if (isImpersonated && location.pathname.includes("/superadmin")) {
        // User is impersonating and trying to access superadmin route - redirect to company dashboard
        const companyUrl = getItem("CompanyUrl");
        if (companyUrl) {
          navigate(`/${companyUrl}/index`, { replace: true });
        }
      }
    }
    // JOB-3246: depend on pathname (stable primitive) rather than the whole
    // location object. React Router returns a new `location` reference on every
    // render, which caused this effect to re-fire needlessly.
  }, [location.pathname, requiredRole, navigate]);
  const checkAuthentication = () => {
    try {
      let token = getItem(tokenKey);
      
      // If no token found and we're checking superadmin route, try to restore from hash
      if (!token && tokenKey === "adminToken" && requiredRole === "SuperAdmin") {
        const hash = window.location.hash;
        if (hash && hash.includes('auth=')) {
          try {
            const authParam = hash.split('auth=')[1];
            if (authParam) {
              const decodedData = JSON.parse(atob(authParam));
              // Check if this is superadmin data (from company logout)
              if (decodedData.adminToken) {
                setItem("adminToken", decodedData.adminToken);
                if (decodedData.adminId) {
                  setItem("admin_id", decodedData.adminId);
                }
                if (decodedData.appUrl) {
                  setItem("appUrl", decodedData.appUrl);
                }
                // Clear impersonation flags when restoring superadmin
                removeItem("isImpersonated");
                removeItem("impersonatedBy");
                removeItem("CompanyId");
                removeItem("CompanyUrl");
                // Remove hash from URL
                window.history.replaceState(null, null, window.location.pathname + window.location.search);
                // Get the token we just set
                token = getItem(tokenKey);
              }
            }
          } catch (error) {
            console.error("Error restoring from hash:", error);
          }
        }
      }
      
      if (!token) {
        // If impersonating and token not found, try to restore super admin session
        const isImpersonated = getItem("isImpersonated") === "true";
        if (isImpersonated && tokenKey === "adminToken") {
          const oldSession = getItem("oldSession");
          const oldAdminId = getItem("oldAdminId");
          if (oldSession) {
            setItem("adminToken", oldSession);
            if (oldAdminId) {
              setItem("admin_id", oldAdminId);
            }
            removeItem("oldSession");
            removeItem("oldAdminId");
            removeItem("isImpersonated");
            removeItem("impersonatedBy");
            navigate("/superadmin/company", { replace: true });
            return;
          }
        }
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        // If impersonating and token expired, restore super admin session instead of clearing
        const isImpersonated = getItem("isImpersonated") === "true";
        if (isImpersonated && tokenKey === "adminToken") {
          const oldSession = getItem("oldSession");
          const oldAdminId = getItem("oldAdminId");
          if (oldSession) {
            setItem("adminToken", oldSession);
            if (oldAdminId) {
              setItem("admin_id", oldAdminId);
            }
            removeItem("oldSession");
            removeItem("oldAdminId");
            removeItem("isImpersonated");
            removeItem("impersonatedBy");
            navigate("/superadmin/company", { replace: true });
            return;
          }
        }
        cleanupTokenData(tokenKey);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      if (requiredRole) {
        if (requiredRole === "SuperAdmin") {
          // Check if user is impersonating - deny access to superadmin routes while impersonating
          const isImpersonated = decodedToken.IsImpersonated === true || 
                                 getItem("isImpersonated") === "true";
          
          if (isImpersonated) {
            // User is impersonating - DENY access to superadmin routes
            // Redirect to company dashboard (don't logout)
            setIsAuthenticated(false);
            setIsLoading(false);
            const companyUrl = decodedToken.CompanyUrl || getItem("CompanyUrl");
            if (companyUrl) {
              navigate(`/${companyUrl}/index`, { replace: true });
            }
            return;
          }
          
          // Only allow if user is a real super admin (not impersonating)
          if (decodedToken.Role === "Admin" && decodedToken.AdminId) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        } else if (requiredRole === "Customer") {
          if (decodedToken.Role === "Customer" ||
              (decodedToken.IsDonationMode !== undefined && tokenKey === "donorToken")) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        } else if (requiredRole === "Worker") {
          if (decodedToken.Role === "Worker") {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        } else if (requiredRole === "Company") {
          if (decodedToken.Role === "Company") {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        } else {
          if (decodedToken.Role === requiredRole) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        }
      } else {
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Token validation error:", error);
      // Check if impersonating before cleaning up
      const isImpersonated = getItem("isImpersonated") === "true";
      const oldSession = getItem("oldSession");
      const oldAdminId = getItem("oldAdminId");
      
      if (isImpersonated && oldSession && tokenKey === "adminToken") {
        // Restore super admin session instead of clearing
        setItem("adminToken", oldSession);
        if (oldAdminId) {
          setItem("admin_id", oldAdminId);
        }
        removeItem("oldSession");
        removeItem("oldAdminId");
        removeItem("isImpersonated");
        removeItem("impersonatedBy");
        navigate("/superadmin/index", { replace: true });
        return;
      }
      
      cleanupTokenData(tokenKey);
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  };  
  if (isLoading) {
    return (
      <Grid
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
        }}
      >
        <LoaderComponent
          isDonationMode={isDonationMode}
          loader={true}
          variant="spinner"
          height="50"
          width="50"
        />
      </Grid>
    );
  }
  if (!isAuthenticated) {
    // If impersonating and trying to access superadmin routes, redirect to company dashboard instead of login
    if (requiredRole === "SuperAdmin") {
      const isImpersonated = getItem("isImpersonated") === "true";
      const companyUrl = getItem("CompanyUrl");
      if (isImpersonated && companyUrl) {
        // Don't logout - redirect to company dashboard
        navigate(`/${companyUrl}/index`, { replace: true });
        return null;
      }
    }
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};
export default ProtectedRoute;